/**
 * Pivot tab — ordered against spread quantity per garment part, one column per size. The only
 * view in the family that pivots; the rest are row-per-record.
 *
 * The server returns long form, one row per (order, part, size); arranging those cells into a
 * matrix is presentation — every number is already summed in SQL (§4.3). This is the same division
 * Production Monthly Output's Summary tab follows, and the reason `report.yaml`'s `pivot:` block is
 * not used here (it is scoped to that one report).
 *
 * `row_type` '0' is the quantity ordered and carries no part; '1' is the quantity actually spread,
 * per part. The legacy screen shaded the ordered row, and the contrast between the two is the whole
 * point of the report — a part short of its order is the thing a reader is looking for.
 */

import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';

import { DataTable } from '@/components/DataTable';
import { integer } from '@/components/report/groupedColumns';

export interface ByPartRow {
  factory: string;
  mo: string;
  b_no: string;
  batch: string;
  set_group: string;
  mo_type: string;
  article: string;
  working_no: string;
  fpd: string;
  psdd: string;
  crd: string;
  row_type: string;
  part: string | null;
  size: string;
  order_qty: number | null;
  size_qty: number | null;
  part_total: number | null;
}

/** One grid row: an order's ordered line, or one of its parts, with a cell per size. */
interface MatrixRow {
  key: string;
  factory: string;
  mo: string;
  b_no: string;
  batch: string;
  set_group: string;
  mo_type: string;
  article: string;
  working_no: string;
  fpd: string;
  psdd: string;
  crd: string;
  row_type: string;
  part: string;
  order_qty: number | null;
  part_total: number | null;
  sizes: Record<string, number>;
}

/**
 * Sizes are a mixed vocabulary — numeric (`40`), prefixed (`A40`) and lettered (`M`) all occur, and
 * a single order generally uses one of them.
 *
 * Wholly numeric sizes sort as numbers and come first, matching the order the detail and summary
 * queries apply server-side: as text, `10` sorts before `2` and `104` before `12`. Sizes sharing a
 * prefix (`A40`, `A100`) compare on their numeric tail. Everything else falls back to text — no
 * sequence is invented for the letter vocabulary, because `2XS` and `2XL` cannot be told apart by
 * any rule derivable from the data.
 */
function compareSizes(a: string, b: string): number {
  const numericA = /^\d+$/.test(a);
  const numericB = /^\d+$/.test(b);
  if (numericA && numericB) {
    return Number(a) - Number(b);
  }
  if (numericA !== numericB) {
    return numericA ? -1 : 1;
  }
  const na = /(\d+)\s*$/.exec(a);
  const nb = /(\d+)\s*$/.exec(b);
  if (na && nb) {
    const prefixA = a.slice(0, na.index);
    const prefixB = b.slice(0, nb.index);
    if (prefixA === prefixB) {
      return Number(na[1]) - Number(nb[1]);
    }
  }
  return a.localeCompare(b);
}

/**
 * Row-key separator. A character that cannot occur in a factory, MO, set group or part code, so
 * two different rows can never collide into one key — written as an escape rather than a literal,
 * which would make this a binary file to git.
 */
const KEY_SEP = '\u0000';

function groupKey(row: ByPartRow): string {
  return [row.factory, row.mo, row.set_group, row.mo_type, row.row_type, row.part ?? ''].join(KEY_SEP);
}

function buildMatrix(rows: readonly ByPartRow[]): {
  matrix: MatrixRow[];
  sizes: string[];
} {
  const bySize = new Set<string>();
  const grouped = new Map<string, MatrixRow>();

  for (const row of rows) {
    if (row.size !== null && row.size !== undefined && row.size !== '') {
      bySize.add(row.size);
    }
    const key = groupKey(row);
    let entry = grouped.get(key);
    if (entry === undefined) {
      entry = {
        key,
        factory: row.factory,
        mo: row.mo,
        b_no: row.b_no,
        batch: row.batch,
        set_group: row.set_group,
        mo_type: row.mo_type,
        article: row.article,
        working_no: row.working_no,
        fpd: row.fpd,
        psdd: row.psdd,
        crd: row.crd,
        row_type: row.row_type,
        part: row.part ?? '',
        order_qty: row.order_qty,
        part_total: row.part_total,
        sizes: {},
      };
      grouped.set(key, entry);
    }
    if (row.size) {
      entry.sizes[row.size] = (entry.sizes[row.size] ?? 0) + (row.size_qty ?? 0);
    }
  }

  const matrix = [...grouped.values()].sort(
    (a, b) =>
      a.factory.localeCompare(b.factory)
      || a.mo.localeCompare(b.mo)
      || a.set_group.localeCompare(b.set_group)
      || a.mo_type.localeCompare(b.mo_type)
      // Ordered line first, then its parts — legacy's partmark1 ordering.
      || a.row_type.localeCompare(b.row_type)
      || a.part.localeCompare(b.part),
  );

  return { matrix, sizes: [...bySize].sort(compareSizes) };
}

function qty(value: number | null | undefined): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground/40">—</span>;
  }
  return integer.format(value);
}

interface ByPartTableProps {
  rows: readonly ByPartRow[];
  isLoading?: boolean;
}

export function ByPartTable({ rows, isLoading }: ByPartTableProps) {
  const { matrix, sizes } = useMemo(() => buildMatrix(rows), [rows]);

  const columns = useMemo<ColumnDef<MatrixRow>[]>(() => {
    const dimension = (
      id: keyof MatrixRow,
      header: string,
    ): ColumnDef<MatrixRow> => ({
      id,
      accessorKey: id,
      header,
      cell: ({ row }) => String(row.original[id] ?? ''),
    });

    return [
      dimension('factory', 'Factory'),
      dimension('mo', 'MO'),
      dimension('b_no', 'B No'),
      dimension('batch', 'Batch'),
      dimension('set_group', 'Set group'),
      // The code, not a label: the legacy label table is dropped by this port and no dict carries
      // the mapping, so the nine codes are with the owner for English wording (improvements.md).
      dimension('mo_type', 'MO type'),
      dimension('article', 'Article'),
      {
        id: 'part',
        accessorKey: 'part',
        header: 'Part',
        // The benchmark row has no part — the order is for garments, not panels — so the cell names
        // what the line is. Legacy put the MO number here and shaded the row; the shared DataTable
        // has no row-level styling hook, so this label is the only thing setting the row apart.
        cell: ({ row }) =>
          row.original.row_type === '0'
            ? <span className="font-medium">Order Qty</span>
            : row.original.part,
      },
      {
        id: 'order_qty',
        accessorKey: 'order_qty',
        header: 'MO Order Qty',
        meta: { align: 'right' },
        cell: ({ row }) => qty(row.original.order_qty),
      },
      {
        id: 'sizes',
        header: 'Size',
        columns: sizes.map<ColumnDef<MatrixRow>>((size) => ({
          id: `size:${size}`,
          header: size,
          // Not sortable: a shared link must not fail when a size disappears from the data, and the
          // server whitelist cannot cover columns it does not know about.
          enableSorting: false,
          meta: { align: 'right', exportLabel: size, exportGroup: 'Size' },
          cell: ({ row }) => qty(row.original.sizes[size]),
        })),
      },
      {
        id: 'part_total',
        accessorKey: 'part_total',
        header: 'Total',
        meta: { align: 'right', exportLabel: 'Total' },
        cell: ({ row }) => <span className="font-medium">{qty(row.original.part_total)}</span>,
      },
    ];
  }, [sizes]);

  // No totals row. Every size column here holds both the ordered line and the parts spread against
  // it, so a column sum would add an order to its own fulfilment and report roughly double. Legacy
  // summed them together in its footer; that is recorded in improvements.md rather than reproduced.
  return (
    <DataTable
      columns={columns}
      data={matrix}
      isLoading={isLoading}
      virtualized
      getRowId={(row) => row.key}
      emptyMessage="No spreading recorded for these filters. Widen the date range or clear the table filter."
    />
  );
}
